import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import FieldShell from '../shared/TailwindComponents/FieldShell';
import { PiTrashLight } from 'react-icons/pi';

interface AddSourceModalContentProps {
  sourceType: 'law' | 'case';
  onConfirm: (data: { sourceData: any; file?: File; files?: File[] }) => void;
  onCancel: () => void;
}

/** A source's title is the uploaded file's name without its extension. */
const nameFromFile = (file: File): string => file.name.replace(/\.[^/.]+$/, '');

/**
 * Upload legislation or precedents into a matter.
 *
 * There is no "name" field. It used to sit above the uploader, auto-filling
 * from the file and wrapping its own label onto two lines (UI feedback M-12) -
 * a second thing to fill in that only ever restated the file you had already
 * chosen. The uploaded file's name IS the source's name now, so the form is
 * just the uploader, a one-line note, and the actions, on one consistent
 * padding grid and the app's own tokens rather than raw greys.
 */
const AddSourceModalContent: React.FC<AddSourceModalContentProps> = ({ sourceType, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);

  const isLaw = sourceType === 'law';

  const handleAdd = () => {
    if (files.length === 0) return;
    if (files.length === 1) {
      onConfirm({ sourceData: { title: nameFromFile(files[0]) }, file: files[0] });
    } else {
      onConfirm({ sourceData: { title: '' }, files });
    }
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const isFormValid = files.length > 0;

  return (
    <div className="md:min-w-[640px] md:max-w-[640px]">
      <div className="space-y-4 px-5">
        <FieldShell
          translate={false}
          label={
            files.length > 1
              ? t('Upload documents') + ` (${files.length})`
              : t('Upload document')
          }
        >
          <FileUploader files={files} onFilesChange={setFiles} />
        </FieldShell>

        {/* File list for multi-file uploads */}
        {files.length > 1 && (
          <div className="space-y-1.5 max-h-40 overflow-y-auto custom-scrollbar-main">
            {files.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between gap-2 px-3 py-2 bg-surface-sunken/70 border border-hairline-soft rounded-md">
                <span className="text-xs text-fg-body truncate flex-1">
                  {file.name}
                </span>
                <span className="text-xs text-fg-muted flex-shrink-0 tabular-nums">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
                <button
                  type="button"
                  onClick={() => removeFile(idx)}
                  aria-label={t('Remove')}
                  className="text-fg-muted hover:text-danger-500 transition-colors flex-shrink-0"
                >
                  <PiTrashLight className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="p-3 bg-surface-sunken/70 rounded-md border border-hairline-soft">
          <p className="text-xs text-fg-muted leading-relaxed">
            {isLaw
              ? t('Upload one or more law, statute, or regulation documents. They will be automatically evaluated by AI for relevance to your matter.')
              : t('Upload one or more court ruling or precedent documents. They will be automatically evaluated by AI for relevance to your matter.')
            }
          </p>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-hairline flex justify-end gap-2 px-5">
        <Button type="button" onClick={onCancel} variant="neutral" size="small">
          {t('Cancel')}
        </Button>
        <Button
          type="button"
          onClick={handleAdd}
          disabled={!isFormValid}
          variant="primary"
          size="small"
        >
          {files.length > 1
            ? (isLaw ? t('Add laws') : t('Add precedents')) + ` (${files.length})`
            : (isLaw ? t('Add law') : t('Add precedent'))
          }
        </Button>
      </div>
    </div>
  );
};

export default AddSourceModalContent;
